import {isNotNullOrUndefined} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import React, {ReactNode, useRef} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {SEARCH_RESULT_COLOR, SELECTED_SEARCH_RESULT_COLOR} from 'notebook/mediocre/search/SearchConstants';
import {getSearchResultsAtRoot, MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
} from 'notebook/structured-tables/column-sorting/getColumnSortingMenuOptions';
import columnDefinitionToTooltip from 'notebook/structured-tables/columns/columnDefinitionToTooltip';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {
  logColumnAnalytics,
  StructuredTableAnalyticsParams,
} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import {Cell} from 'notebook/tables/constants/CellConstants';
import * as RowConstants from 'notebook/tables/constants/RowConstants';
import type {
  ColumnConstraint,
  InsertableColumnInfo,
  TooltipIcon,
} from 'notebook/tables/constants/TableColumnConstants';
import {USER_CREATED_COLUMN_NAME_MAX_LENGTH} from 'notebook/tables/constants/TableColumnConstants';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {
  SelectionRange,
  TABLE_COLUMN,
  TABLE_ROW,
  TableDimension,
} from 'notebook/tables/constants/TableSelectionConstants';
import TableAxisCellDragHandle from 'notebook/tables/core-ui/TableAxisCellDragHandle';
import excess from 'notebook/tables/excess';
import {useAxisCellModel} from 'notebook/tables/store/hooks/useAxisCellModel';
import {AxisCellModel, isColumnHeaderModel} from 'notebook/tables/store/models/AxisCellModel';
import TableUtil from 'notebook/tables/TableUtil';
import getDimensionalOperationText from 'notebook/tables/util/getDimensionalOperationText';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BLUE_LIGHT} from 'ui-library/Colors';
import {ToggleDisplayMode} from 'ui-library/floating-ui/Float';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as modals from 'util/Modals';
import removeNulls from 'util/removeNulls';
import {BasicEvent, ElementMouseEvent} from 'util/Types';

// TODO(BNCH-108093): The TableAxisCell component currently decides the ordering of all the context menu
// options, but in a very strange way. This needs to be fixed so that reordering these options is much easier.
// These constants are here rather than their owning Context Options to prevent circular imports.
export const SINGLE_INPUT_CONTEXT_MENU_OPTION_ID = 'structured-table-single-input';
export const ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID = 'structured-table-add-edit-table-mapping';
export const REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID = 'structured-table-remove-table-mapping';
export const ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID = 'structured-table-add-from-structured-table';
export const CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID = 'change-custom-column-type';

// When a table is protected because it's a fill-in entry, we should only allow column context menu options
// that cannot change the structure of the table.
export const ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED = [
  ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
  SORT_ASCENDING_ID,
  SORT_DESCENDING_ID,
];

export const INSERT_CUSTOM_COLUMN_LEFT = 'Insert custom column left';
export const INSERT_CUSTOM_SINGLE_INPUT_LEFT = 'Insert custom single input left';
export const INSERT_CUSTOM_COLUMN_RIGHT = 'Insert custom column right';
export const INSERT_CUSTOM_SINGLE_INPUT_RIGHT = 'Insert custom single input right';

export type AxisType = TableDimension;
type Direction = 'BEFORE' | 'AFTER';

export const enum DIRECTION {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

const ARCHIVED_ROW_TOOLTIP_FIRST_LINE = 'Archived';
export const UNSUBMITTED_CHANGES_TOOLTIP = 'Please submit table'; // exported for test
const TOMBSTONE_REASON_TOOLTIP = 'Row is invalid. Please adjust any formulas before deleting row.';
const ROW_ERROR_TOOLTIP = 'Error in row';

export type MenuOption =
  | '-' // specifically, a menu separator
  | {
      name: string;
      click: (e: ElementMouseEvent) => void;
      hide: () => boolean;
      disabled?: boolean;
    };

export type MenuOptions = Array<MenuOption>;

export interface AnalyticsParams {
  fileAnalyticsParams: FileAnalyticsParams | null;
  structuredTableAnalyticsParams: StructuredTableAnalyticsParams | null;
}

export interface TableAxisCellProps extends MediocreSearchResultProps {
  /**
   * When true, the cell will show the excess label, e.g. "A" or "1", instead of the custom label.
   * Primarily used when the user is interacting with a formula. Threaded from SpreadsheetEvalMixin.
   */
  showExcessLabels: boolean;
  toggleHeader?: () => void;
  // delDim, insertDimBefore and insertDimAfter are not required since they may not be specified if
  // the table is not editable
  delDim?: ((minIndex: number | null, maxIndex: number | null) => void) | null;
  renameDim?: (index: number, name: string) => void;
  insertDimBefore?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
  insertDimAfter?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
  /** Pre-computed selection range for this axis cell. */
  selectionRange?: SelectionRange | null;
  numDims: number; // number of the dim in the table
  deleteTable?: () => void;
  isTableSelected: boolean;
  allowEmpty?: boolean;
  disallowCustomColumns: boolean;
  cellDragStart?: (
    options: {start: [number, number] | null; active: string},
    event: React.MouseEvent
  ) => void;
  cellDragOver?: (index: [number, number] | null, e: React.MouseEvent) => void;
  showDragHandle?: boolean;
  dragHandleMouseDown?: (draggingDimension: TableDimension, index: number) => boolean;
  isFirstColumn?: boolean;
  isSelected: boolean;
  columnConstraint?: ColumnConstraint;
  rowData?: RowConstants.RowData | null;
  rowProperties?: RowConstants.RowProperties | null;
  isLeftAlign: boolean;
  isSingleInputHeader?: boolean;
  insertableColumnInfos?: Array<InsertableColumnInfo>;
  // Method that given the selection range returns additional menu items
  // to display in the context menu. The items are of the form:
  //  name: string,
  //  click: func,
  //  hide: func,
  //  displayGroup: ContextMenuDisplayGroup.TOP | ContextMenuDisplayGroup.MIDDLE | ContextMenuDisplayGroup.BOTTOM
  // or a '-' to represent a separator.
  getExtraContextMenuOptions?: (
    selectionRange: SelectionRange | null
  ) => Array<contextmenu.ContextMenuOption> | null;
  // For performance, we pass this as a function rather than an array of strings, so this prop
  // should not be used during render!
  getDisallowedColumnNames?: () => Array<string>;
  shouldDisableDeleteDim: boolean;
  shouldDisableInsertDimBefore: boolean;
  shouldDisableInsertDimAfter: boolean;
  disableInsertDimTooltip?: string;
  finalizeAndExitEditMode?: () => void;
  locked: boolean;
  isProtected: boolean;
  disableColumnLocking?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /**
   * isConfig indicates if this cell is in a template, it will be used to determine if the column locking
   * options should be shown or not.
   */
  isConfig?: boolean;
  setColumnConstraint?: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell: Partial<Cell>
  ) => void;

  // Has the row been in the view port of the browser yet?
  hasRowBeenInView?: boolean;

  // Affixed Column
  isAffixed?: boolean; // Whether the column is sticky.  Default is false
  showAffixedShadow?: boolean; // Only enabled when a column is sticky and scrolled.  Default is false.
  affixedLeftSpacing?: number; // Must be specified if isAffixed = true

  isLastFrozenColumn?: boolean;
  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
}

// ---------------------------------------------------------------------------
// Pure utility functions (exported for direct use and testability)
// ---------------------------------------------------------------------------

export function getSelectedCount(selectionRange: SelectionRange | null): number {
  if (selectionRange == null) {
    return 0;
  }
  return selectionRange.maxIndex - selectionRange.minIndex + 1;
}

export function isInSelectionRange(
  selectionRange: SelectionRange | null,
  index: number | undefined
): boolean {
  if (selectionRange != null && index !== undefined) {
    return selectionRange.minIndex <= index && index <= selectionRange.maxIndex;
  }
  return false;
}

export function getStartIndex(dim: AxisType, index: number): [number, number] {
  switch (dim) {
    case TABLE_ROW:
      return [index, 0];
    case TABLE_COLUMN:
      return [0, index];
    default:
      assert(false, `Unexpected dimension ${dim}`);
  }
}

/**
 * We only show the "excess label", e.g. "A" or "1" if we are in "formula mode" (showExcessLabels),
 * or there is no header text (customLabel == null).
 */
export function shouldHideExcessLabels(showExcessLabels: boolean, customLabel: ReactNode): boolean {
  return !showExcessLabels && customLabel != null;
}

export function getExcessLabel(dim: AxisType, index: number): string {
  return dim === TABLE_ROW
    ? String(excess.util.rowIndexToNumber(index))
    : excess.util.colNumberToLetters(index);
}

export function getTooltipTitle(
  dim: AxisType,
  columnConstraint: ColumnConstraint | undefined,
  tooltipIcons: Array<TooltipIcon> | null | undefined,
  rowProperties: RowConstants.RowProperties | null | undefined
): string {
  if (dim === TABLE_COLUMN) {
    let tooltips: Array<string | null | undefined> = [columnConstraint?.tooltip];
    if (columnConstraint?.columnDefinition) {
      tooltips.push(columnDefinitionToTooltip(columnConstraint.columnDefinition));
    }

    const allTooltipIcons = (tooltipIcons || columnConstraint?.tooltipIcons) ?? [];
    tooltips = tooltips.concat(allTooltipIcons.map(({tooltip}) => tooltip));

    return tooltips.filter((option) => isNotNullOrUndefined(option)).join(', ');
  } else {
    if (rowProperties?.archivePurpose != null) {
      return `${ARCHIVED_ROW_TOOLTIP_FIRST_LINE}\n${rowProperties.archivePurpose}`;
    } else if (rowProperties?.isTombstoned) {
      return rowProperties?.tombstoneReasonTooltip || TOMBSTONE_REASON_TOOLTIP;
    } else if (!rowProperties?.hasErrors && rowProperties?.hasUnsubmittedChanges) {
      return rowProperties?.unsubmittedChangesTooltip ?? UNSUBMITTED_CHANGES_TOOLTIP;
    } else if (rowProperties?.hasErrors) {
      return ROW_ERROR_TOOLTIP;
    }
    return '';
  }
}

export function getValidationIndicator(
  rowData: RowConstants.RowData | null | undefined,
  columnConstraint: ColumnConstraint | undefined
): ReactNode {
  assert(
    !(Boolean(rowData) && Boolean(columnConstraint)),
    'TableAxisCell should have rowData if it appears in the primary sample column and a columnConstraint ' +
      'if it appears in the header row, but not both at the same time'
  );
  if (rowData) {
    return rowData.uiData ? rowData.uiData.userAnnotationIndicator : null;
  }
  if (columnConstraint) {
    return columnConstraint.userAnnotationIndicator;
  }
  return null;
}

export function getCellStyle(
  isAffixed: boolean | undefined,
  affixedLeftSpacing: number | undefined,
  searchResultProps: MediocreSearchResultProps
): React.CSSProperties {
  const cellStyles: React.CSSProperties = {};
  if (isAffixed) {
    cellStyles.left = `${affixedLeftSpacing}px`;
  }
  const {results, selected} = getSearchResultsAtRoot(searchResultProps);
  if (selected) {
    cellStyles.backgroundColor = SELECTED_SEARCH_RESULT_COLOR;
  } else if (results.length > 0) {
    cellStyles.backgroundColor = SEARCH_RESULT_COLOR;
  }
  return cellStyles;
}

/**
 * When ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION is enabled, tenant admins can lock and unlock columns
 * via the context menu even in execution context (e.g. entries), where lock/unlock is normally hidden.
 */
export function canModifyColumnLockAsAdmin(): boolean {
  return (
    BenchlingConfig.ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION &&
    Boolean(EntityStore.currentUser()?.isTenantAdmin)
  );
}

export function canSetProtected(
  isConfig: boolean | undefined,
  selectionRange: SelectionRange | null
): boolean {
  if (selectionRange == null) {
    return false;
  }
  return (
    (Boolean(isConfig) || canModifyColumnLockAsAdmin()) && selectionRange.maxIndex === selectionRange.minIndex
  );
}

/** Return the context menu delete column/row[s] text. */
export function getContextMenuDeleteDimsText(
  columnConstraint: ColumnConstraint | undefined,
  selectionRange: SelectionRange | null,
  dim: AxisType
): string {
  const verb = columnConstraint?.viewer ? 'Hide' : 'Delete';
  return getDimensionalOperationText({
    verb,
    selectionRange,
    dimension: dim,
  });
}

/** Return the context menu insert column/row[s] text. */
export function getContextMenuInsertDimsText(
  dim: AxisType,
  direction: Direction,
  selectionRange: SelectionRange | null,
  disallowCustomColumns: boolean,
  // When true, the column noun is replaced with "single input" so that the menu reads
  // "Insert single input left" instead of "Insert column left".
  isSingleInput: boolean = false
): string {
  const dimension = isSingleInput ? 'single input' : dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
  const directionText = {
    [TABLE_ROW]: {
      [DIRECTION.BEFORE]: 'above',
      [DIRECTION.AFTER]: 'below',
    },
    [TABLE_COLUMN]: {
      [DIRECTION.BEFORE]: 'left',
      [DIRECTION.AFTER]: 'right',
    },
  }[dim][direction];

  const numSelected = getSelectedCount(selectionRange);
  const allowMultiple = !disallowCustomColumns;
  if (numSelected === 1 || !allowMultiple) {
    return `Insert ${dimension} ${directionText}`;
  } else {
    return `Insert ${numSelected} ${dimension}s ${directionText}`;
  }
}

/**
 * Returns a copy of the given columnInfo with its column marked as a Single Input, so that the
 * inserted column is rendered as a Single Input. Returns the columnInfo unchanged if it does not
 * carry a structured table column (e.g. when inserting a blank/custom column).
 */
export function withSingleInputColumnInfo(columnInfo: InsertableColumnInfo | {}): InsertableColumnInfo | {} {
  const info = columnInfo as Partial<InsertableColumnInfo>;
  const structuredTableColumn = info.columnConstraints?.structuredTableColumn;
  if (structuredTableColumn == null) {
    return columnInfo;
  }
  return {
    ...info,
    columnConstraints: {
      ...info.columnConstraints,
      structuredTableColumn: {
        ...structuredTableColumn,
        isSingleInput: true,
      },
    },
  };
}

export function willInsertExceedTableMaxDims(dim: AxisType, numDims: number, selectedCount: number): boolean {
  if (dim === TABLE_ROW) {
    return numDims + selectedCount > getMaxRowsConfig(true);
  } else {
    return numDims + selectedCount > TableLayoutConstants.TABLE_MAX_COLUMNS;
  }
}

export function getContextMenuInsertDimsExceedMaxText(dim: AxisType): string {
  const dimension = dim === TABLE_ROW ? TABLE_ROW : TABLE_COLUMN;
  const maxNumDims = dim === TABLE_ROW ? getMaxRowsConfig(true) : TableLayoutConstants.TABLE_MAX_COLUMNS;
  return `Total ${dimension}s after insert cannot exceed ${maxNumDims}`;
}

export function hasVisibleContextOptions(
  isGhostCell: boolean,
  contextMenuOptions: Array<contextmenu.ContextMenuOption>
): boolean {
  // Ghost cells in plain tables should never show context options/menus.
  if (isGhostCell) {
    return false;
  }

  function filterFunc(option: string | MenuOption | contextmenu.ClickableContextMenuOption | null): boolean {
    return typeof option === 'object' && !_.result(option, 'hide');
  }

  return contextMenuOptions.some((option) => filterFunc(option));
}

/**
 * Builds context menu options from component props. Extracted so that the menu
 * structure is testable independently of the rendered component.
 */
export function getContextMenuOptions(
  {
    allowEmpty = false,
    columnConstraint,
    deleteTable,
    delDim,
    disableColumnLocking,
    disableInsertDimTooltip,
    disallowCustomColumns,
    getDisallowedColumnNames,
    getExtraContextMenuOptions,
    insertableColumnInfos,
    insertDimAfter,
    insertDimBefore,
    isConfig,
    isProtected,
    isSingleInputHeader,
    isTableSelected,
    locked,
    numDims,
    renameDim,
    setColumnConstraint,
    freezeThroughColumn,
    unfreezeFromColumn,
    isAffixed,
    shouldDisableDeleteDim,
    shouldDisableInsertDimAfter,
    shouldDisableInsertDimBefore,
    toggleHeader,
  }: TableAxisCellProps,
  axisCellModel: AxisCellModel,
  selectionRange: SelectionRange | null,
  analyticsParams?: AnalyticsParams | null
): Array<contextmenu.ContextMenuOption> {
  const {dim, index} = axisCellModel;
  // Only columns can be renamed, so the row axis never has an editable name to rename from.
  const label = isColumnHeaderModel(axisCellModel) ? axisCellModel.label : null;
  // When in Fill-In mode, the column context menu options are limited
  const isTableProtected = dim === TABLE_COLUMN && isProtected;
  const isReadOnly = columnConstraint?.structuredTableColumn?.isReadOnly ?? false;
  const isDimensionProtected = columnConstraint?.isProtected ?? false;
  const allExtraMenuOptions = getExtraContextMenuOptions?.(selectionRange) ?? [];
  // Filter out any nulls, strings, or separators so that we only have clickable options
  let extraMenuOptions = allExtraMenuOptions.filter(
    (option): option is contextmenu.ClickableContextMenuOption => option !== '-'
  );
  // Filter out any options that are not allowed when the table is protected
  if (isTableProtected) {
    extraMenuOptions = extraMenuOptions.filter((option) =>
      ALLOWED_COLUMN_CONTEXT_MENU_OPTION_IDS_WHEN_PROTECTED.includes(option.id ?? '')
    );
  }

  // any menuOptions not specifying a displayGroup will be placed at the top
  const extraMenuOptionsAtTop = extraMenuOptions.filter(
    (option) =>
      (!option.displayGroup || option.displayGroup === contextmenu.ContextMenuDisplayGroup.TOP) &&
      // Change column type option is manually positioned after rename, so exclude it from the top group
      option.id !== CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID
  );
  // Change column type option should appear right after rename
  const changeColumnTypeOption = extraMenuOptions.find(
    (option) => option.id === CHANGE_CUSTOM_COLUMN_TYPE_CONTEXT_MENU_OPTION_ID
  );
  const menuOptionIdsMiddleUpper = [
    ADD_FROM_STRUCTURED_TABLE_CONTEXT_MENU_OPTION_ID,
    ADD_EDIT_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
    REMOVE_TABLE_MAPPING_CONTEXT_MENU_OPTION_ID,
    SINGLE_INPUT_CONTEXT_MENU_OPTION_ID,
  ];
  const extraMenuOptionsAtMiddleUpper = extraMenuOptions.filter(
    (option) =>
      option.id &&
      menuOptionIdsMiddleUpper.includes(option.id) &&
      option.displayGroup === contextmenu.ContextMenuDisplayGroup.MIDDLE
  );
  const extraMenuOptionsAtMiddleLower = extraMenuOptions.filter(
    (option) =>
      (!option.id || !menuOptionIdsMiddleUpper.includes(option.id)) &&
      option.displayGroup === contextmenu.ContextMenuDisplayGroup.MIDDLE
  );
  const menuOptionsBottomUpper = [
    INSERT_CUSTOM_COLUMN_LEFT,
    INSERT_CUSTOM_SINGLE_INPUT_LEFT,
    INSERT_CUSTOM_COLUMN_RIGHT,
    INSERT_CUSTOM_SINGLE_INPUT_RIGHT,
  ];
  const extraMenuOptionsAtBottomUpper = extraMenuOptions.filter(
    (option) =>
      menuOptionsBottomUpper.includes(option.name) &&
      option.displayGroup === contextmenu.ContextMenuDisplayGroup.BOTTOM
  );
  const extraMenuOptionsAtBottomLower = extraMenuOptions.filter(
    (option) =>
      !menuOptionsBottomUpper.includes(option.name) &&
      option.displayGroup === contextmenu.ContextMenuDisplayGroup.BOTTOM
  );

  // --- Handler closures ---

  // This closure should be exposed as props.onRenameModal to avoid handling special cases (of disallowed
  // names, and whether or not the label can be reset).
  const openRenameModal = (): void => {
    const disallowedColumnNames = getDisallowedColumnNames ? getDisallowedColumnNames() : [];
    const validate = (name: string): boolean =>
      name === label ||
      (!disallowedColumnNames.includes(name) && name.length <= USER_CREATED_COLUMN_NAME_MAX_LENGTH);

    // A column label can be reset if it is explicitly set.
    let canReset = columnConstraint ? columnConstraint.canResetLabel : null;
    if (canReset == null) {
      canReset = label != null;
    }

    modals.prompt('Rename column', label, validate, {canReset}).then((name) => {
      if (renameDim && index != null && name != null) {
        renameDim(index, name);
      }
    });
  };

  const delDimHandler = (): void => {
    // TODO(T7364): Check that delDim is defined since the props can change after the context
    // menu is opened.
    if (selectionRange != null && typeof delDim === 'function') {
      delDim(selectionRange.minIndex, selectionRange.maxIndex);
      if (dim === TableDimension.COLUMN && analyticsParams) {
        logColumnAnalytics({
          columnConstraint,
          analyticsEvent: ELN_ANALYTICS_ACTIONS.DELETE_COLUMN,
          structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
          fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
        });
      }
    }
  };

  const handleSetProtected = (newIsProtected: boolean): void => {
    if (!setColumnConstraint) {
      return;
    }

    if (columnConstraint?.structuredTableColumn) {
      setColumnConstraint(
        index,
        reactUpdate(columnConstraint, {
          structuredTableColumn: {$merge: {isProtected: newIsProtected}},
          $merge: {isProtected: newIsProtected},
        }),
        {}
      );
    } else {
      setColumnConstraint(
        index,
        reactUpdate(columnConstraint || {}, {$merge: {isProtected: newIsProtected}}),
        {}
      );
    }
    if (analyticsParams) {
      logColumnAnalytics({
        columnConstraint,
        analyticsEvent: ELN_ANALYTICS_ACTIONS.TOGGLED_COLUMN_LOCKING,
        structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
        fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
        additionalMetadata: {
          setting: newIsProtected ? 'lock' : 'unlock',
        },
      });
    }
  };

  // If we can only insert specific columns, not custom columns, then the "insert multiple columns"
  // UI (selecting multiple columns and inserting) should instead prompt to insert a single column,
  // and we insert to the left/right of the whole selection.
  const shouldAllowMultiple = !disallowCustomColumns;

  // For Single Input headers, the inserted column should itself be a Single Input.
  const resolveColumnInfo = (columnInfo: InsertableColumnInfo | {}): InsertableColumnInfo | {} =>
    isSingleInputHeader ? withSingleInputColumnInfo(columnInfo) : columnInfo;

  const insertDimBeforeHandler = (e: BasicEvent, columnInfo: InsertableColumnInfo | {} = {}): void => {
    // TODO(T7364): Check that insertDimBefore is defined since the props can change after the
    // context menu is opened.
    if (selectionRange != null && typeof insertDimBefore === 'function') {
      const resolvedColumnInfo = resolveColumnInfo(columnInfo);
      if (shouldAllowMultiple) {
        insertDimBefore(selectionRange.minIndex, selectionRange.maxIndex, resolvedColumnInfo);
      } else {
        // If we aren't allowed to insert multiple, just insert one before the selection start.
        insertDimBefore(selectionRange.minIndex, selectionRange.minIndex, resolvedColumnInfo);
      }
    }
  };

  const insertDimAfterHandler = (e: BasicEvent, columnInfo?: InsertableColumnInfo): void => {
    // TODO(T7364): Check that insertDimAfter is defined since the props can change after the
    // context menu is opened.
    if (selectionRange != null && typeof insertDimAfter === 'function') {
      const resolvedColumnInfo = resolveColumnInfo(columnInfo || {});
      if (shouldAllowMultiple) {
        insertDimAfter(selectionRange.minIndex, selectionRange.maxIndex, resolvedColumnInfo);
      } else {
        // If we aren't allowed to insert multiple, just insert one after the selection end.
        insertDimAfter(selectionRange.maxIndex, selectionRange.maxIndex, resolvedColumnInfo);
      }
    }
  };

  // --- Submenu builders ---

  const selectedCount = getSelectedCount(selectionRange);

  const buildInsertableColumnInfosMenu = (
    insertHandler: (e: BasicEvent, columnInfo: InsertableColumnInfo) => void,
    direction: Direction
  ): null | Array<{
    name: string;
    click: (e: ElementMouseEvent) => void;
    dataTestAttrs: DataTestAttrs;
  }> => {
    // Only allow inserting one field at a time if special field is to be inserted
    if (
      !insertableColumnInfos ||
      insertableColumnInfos.length === 0 ||
      (selectedCount !== 1 && shouldAllowMultiple)
    ) {
      return null;
    }
    return insertableColumnInfos.map((columnInfo) => ({
      name: columnInfo.columnLabels,
      click: (e: ElementMouseEvent) => {
        insertHandler(e, columnInfo);
      },
      dataTestAttrs: {
        component: 'ContextMenu',
        element: direction === DIRECTION.BEFORE ? 'insertColumnBefore' : 'insertColumnAfter',
        key: encodeURIComponent(columnInfo.columnLabels).replace(/[^A-Z0-9]/gi, ''),
      },
    }));
  };

  const buildInsertDimMenu = (
    direction: Direction
  ): {
    name: string;
    disabled: boolean;
    click?: (e: ElementMouseEvent) => void;
    menu?: null | Array<{name: string; click: (e: ElementMouseEvent) => void}>;
    tooltip?: string | null;
    dataTestAttrs: DataTestAttrs;
  } => {
    const name = getContextMenuInsertDimsText(
      dim,
      direction,
      selectionRange,
      disallowCustomColumns,
      Boolean(isSingleInputHeader)
    );
    const dataTestAttrs = {component: 'ContextMenu', element: 'insertDimMenu', key: direction};
    const insertHandler = direction === DIRECTION.BEFORE ? insertDimBeforeHandler : insertDimAfterHandler;
    if (willInsertExceedTableMaxDims(dim, numDims, selectedCount)) {
      return {
        name,
        disabled: true,
        tooltip: getContextMenuInsertDimsExceedMaxText(dim),
        dataTestAttrs,
      };
    } else {
      const isDisabled =
        direction === DIRECTION.BEFORE ? shouldDisableInsertDimBefore : shouldDisableInsertDimAfter;
      return {
        name,
        click: insertHandler,
        menu: isDisabled ? null : buildInsertableColumnInfosMenu(insertHandler, direction),
        disabled: isDisabled,
        tooltip: isDisabled ? disableInsertDimTooltip : null,
        dataTestAttrs,
      };
    }
  };

  // --- Assemble menu ---

  return removeNulls([
    ...extraMenuOptionsAtTop,
    '-',
    {
      name: `Rename ${dim}`,
      click: openRenameModal,
      hide: () =>
        (columnConstraint && (columnConstraint.editLabelDisabled || columnConstraint.viewer)) ||
        renameDim == null ||
        index == null ||
        selectionRange == null ||
        isTableProtected,
      disabled: isDimensionProtected,
      tooltip: isDimensionProtected ? 'Cannot rename a locked column' : null,
      dataTestAttrs: {component: 'ContextMenu', element: 'renameDim'},
    },
    // Change column type option appears right after rename (for custom columns only)
    changeColumnTypeOption ?? null,
    {
      name: 'Toggle header',
      click: toggleHeader,
      hide: () => toggleHeader == null,
    },
    {
      name: 'Delete table',
      click: deleteTable,
      hide: () => deleteTable == null || !isTableSelected || locked || isProtected || shouldDisableDeleteDim,
    },
    {
      name: getContextMenuDeleteDimsText(columnConstraint, selectionRange, dim),
      click: () => {
        delDimHandler();
      },
      hide: () => delDim == null || (isTableSelected && !allowEmpty) || isTableProtected,
      disabled: shouldDisableDeleteDim || isDimensionProtected,
      dataTestAttrs: {component: 'ContextMenu', element: 'deleteDim'},
    },
    '-',
    {
      name: 'Lock column',
      click: () => {
        handleSetProtected(true);
      },
      hide: () =>
        isDimensionProtected ||
        !canSetProtected(isConfig, selectionRange) ||
        disableColumnLocking ||
        locked ||
        isTableProtected,
      disabled: isReadOnly,
      tooltip: isReadOnly ? 'Cannot lock a read only column' : null,
    },
    {
      name: 'Unlock column',
      click: () => {
        handleSetProtected(false);
      },
      hide: () =>
        !isDimensionProtected || !canSetProtected(isConfig, selectionRange) || locked || isTableProtected,
      disabled: isReadOnly,
    },
    {
      name: 'Freeze up to this column',
      click: () => {
        if (index != null && freezeThroughColumn) {
          freezeThroughColumn(index);
        }
      },
      hide: () =>
        dim !== TABLE_COLUMN ||
        locked ||
        isTableProtected ||
        freezeThroughColumn == null ||
        index == null ||
        Boolean(isAffixed) ||
        columnConstraint?.structuredTableColumn?.isSingleInput === true,
      dataTestAttrs: {component: 'ContextMenu', element: 'freezeColumn'},
    },
    {
      name: 'Unfreeze from this column',
      click: () => {
        if (index != null && unfreezeFromColumn) {
          unfreezeFromColumn(index);
        }
      },
      hide: () =>
        dim !== TABLE_COLUMN ||
        locked ||
        isTableProtected ||
        unfreezeFromColumn == null ||
        index == null ||
        !isAffixed ||
        columnConstraint?.structuredTableColumn?.isSingleInput === true,
      dataTestAttrs: {component: 'ContextMenu', element: 'unfreezeColumn'},
    },
    ...extraMenuOptionsAtMiddleUpper,
    '-',
    insertDimBefore && !isTableProtected ? buildInsertDimMenu('BEFORE') : null,
    insertDimAfter && !isTableProtected ? buildInsertDimMenu('AFTER') : null,
    '-',
    ...extraMenuOptionsAtMiddleLower,
    '-',
    ...extraMenuOptionsAtBottomUpper,
    '-',
    ...extraMenuOptionsAtBottomLower,
  ]);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function TableAxisCellComponent(props: TableAxisCellProps): JSX.Element {
  const axisCellModel = useAxisCellModel();
  const {dim, index, isGhostCell, customLabel, isHeaderCell} = axisCellModel;
  // The column-only parts of the model. Their defaults here are what the row axis renders with.
  const columnHeader = isColumnHeaderModel(axisCellModel) ? axisCellModel : null;
  const iconPrefix = columnHeader?.iconPrefix ?? null;
  const tooltipIcons = columnHeader?.tooltipIcons ?? null;
  const shouldHighlightCell = columnHeader?.shouldHighlightCell ?? false;
  const columnIsOutdated = columnHeader?.columnIsOutdated ?? false;
  const {
    showExcessLabels,
    isLeftAlign,
    isSelected,
    columnConstraint,
    rowData,
    rowProperties,
    isSingleInputHeader,
    isFirstColumn,
    isLastFrozenColumn,
    isAffixed,
    showAffixedShadow,
    affixedLeftSpacing,
    showDragHandle,
    dragHandleMouseDown,
    cellDragStart,
    cellDragOver,
    finalizeAndExitEditMode,
    selectionRange = null,
    hasRowBeenInView,
    dataTestAttrs,
  } = props;

  const contextMenuButtonRef = useRef<HTMLSpanElement>(null);
  const {rootFolderId: projectId, fileId, templateCollectionId} = EditorPropertiesContext.useContext();
  const {structuredTableKind, provenanceUuid} = StructuredTableContext.useContext();

  const analyticsParams: AnalyticsParams = {
    structuredTableAnalyticsParams:
      structuredTableKind != null
        ? {structuredTableKind, structuredTableProvenanceUuid: provenanceUuid!}
        : null,
    fileAnalyticsParams: {fileId, projectId, templateCollectionId},
  };

  // --- Event handlers ---

  const handleCellDragStart = (e: ElementMouseEvent): void => {
    finalizeAndExitEditMode?.();

    if (cellDragStart == null) {
      return;
    }
    // Return if the button was initiated by an open contextmenu click
    const fromMenuClick = contextMenuButtonRef.current
      ? $(e.target).closest(contextMenuButtonRef.current).length !== 0
      : false;
    if ((fromMenuClick || e.button !== 0) && isInSelectionRange(selectionRange, index)) {
      return;
    }
    cellDragStart(
      {
        start: getStartIndex(dim, index),
        active: TableUtil.dimensionToSelection(dim),
      },
      e
    );
  };

  const handleCellDragOver = (e: ElementMouseEvent): void => {
    cellDragOver?.(getStartIndex(dim, index), e);
  };

  const menuOptions = getContextMenuOptions(props, axisCellModel, selectionRange, analyticsParams);
  const hasContextOptions = hasVisibleContextOptions(isGhostCell, menuOptions);

  const handleOpenContextMenu = (e: BasicEvent): void => {
    if (!(selectionRange != null && hasContextOptions)) {
      e.preventDefault();
      return;
    }
    contextmenu.view.open(e, menuOptions, true);
  };

  // --- Render helpers ---

  const resolvedTooltipIcons = tooltipIcons || columnConstraint?.tooltipIcons;

  const renderIcons = (): Array<JSX.Element> | null => {
    if (resolvedTooltipIcons == null) {
      return null;
    }
    return resolvedTooltipIcons.map(({icon}, i) => (
      <span key={i} className="mediocre-tableEditable-axisCell-icon u-vertical-center-items">
        <BenchlingIcon icon={icon} />
      </span>
    ));
  };

  const tooltipTitle = getTooltipTitle(dim, columnConstraint, tooltipIcons, rowProperties);

  const renderPrefixIcon = (renderTooltip: boolean = true): JSX.Element | null => {
    let element: JSX.Element;

    if (columnConstraint?.isProtected) {
      element = (
        <span
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
          })}
        >
          <BenchlingIcon icon="lock" />
        </span>
      );
    } else if (BenchlingConfig.ELN_ENABLE_COLUMN_TYPE_ICONS && iconPrefix) {
      element = (
        <BenchlingIcon
          icon={iconPrefix}
          className={classNames('mediocre-tableEditable-axisCell-prefix-icon', {
            'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
            [css(aphroditeStyles.columnMappingIcon)]: iconPrefix === 'column-mapping',
          })}
        />
      );
    } else {
      return null;
    }

    if (!renderTooltip) {
      return element;
    }

    return (
      <Tooltip
        text={
          // Provide a specific div for the tooltip text to widen it slightly
          <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
        }
        isDisabled={!tooltipTitle}
        placement={FloatingPlacement.TOP}
      >
        {element}
      </Tooltip>
    );
  };

  const renderLabel = (rowInView?: boolean): ReactNode => {
    if (rowInView != null && !rowInView) {
      // Return a skeleton chip until we're in the View and need to render the real value
      return <Chip faded={false} size={ChipSize.MINI} text="&nbsp;" />;
    }

    const hideExcess = shouldHideExcessLabels(showExcessLabels, customLabel);
    const excessLabel = getExcessLabel(dim, index);

    let visibleContent: ReactNode;
    if (hideExcess) {
      visibleContent = customLabel || '\u00a0';
    } else if (customLabel == null) {
      visibleContent = excessLabel;
    } else {
      visibleContent = (
        <>
          {excessLabel} {'\u2013'} {customLabel}
        </>
      );
    }
    return (
      <span
        className="mediocre-tableEditable-axisCell-labelInnerWrapper"
        {...getTestProps({component: 'TableAxisCell', element: 'label'})}
      >
        {visibleContent}
        {columnConstraint?.structuredTableColumn?.isRequired ? (
          <span className="u-danger-color">*</span>
        ) : null}
      </span>
    );
  };

  const renderLabelSizer = (): ReactNode => {
    // Target size is just to fit the label
    if (customLabel) {
      return customLabel;
    }
    // If there is no label, size based on the excess label
    return shouldHideExcessLabels(showExcessLabels, customLabel) ? '\u00a0' : getExcessLabel(dim, index);
  };

  const renderDragHandle = (): ReactNode => {
    if (!showDragHandle || !dragHandleMouseDown) {
      return null;
    }
    return (
      <TableAxisCellDragHandle dim={dim} onMouseDown={() => dragHandleMouseDown?.(dim, index) ?? false} />
    );
  };

  // --- Render ---

  const prefixIcon = renderPrefixIcon();
  const cellStyle = getCellStyle(isAffixed, affixedLeftSpacing, props);

  const content = (
    <>
      {!isSingleInputHeader && renderDragHandle()}
      {getValidationIndicator(rowData, columnConstraint)}
      <div
        className={classNames('mediocre-tableEditable-axisCell-labelWrapper', {
          'is-singleInput-header': isSingleInputHeader,
        })}
      >
        {/* Render the typical label along with any icons, invisibly, to properly size the cell. The
            visible label is absolutely positioned, so the excess code reference ("A –") shown while
            editing a formula wraps within this width instead of widening the cell — matching normal
            table header axis cells (BNCH-156759). */}
        <div className="invisible">{renderPrefixIcon(false)}</div>

        <div
          className={classNames(
            'mediocre-tableEditable-axisCell-labelSizer',
            'u-overflow-wrap-break-word',
            'invisible',
            {
              'mediocre-tableEditable-axisCell--header': isHeaderCell,
            }
          )}
        >
          {renderLabelSizer()}
        </div>
        <div className="u-flex-display invisible">{renderIcons()}</div>
        {/* Depending on hover / excel-mode, render the correct label. This is absolutely positioned,
            so changing the label size does not cause any jank resizes */}
        <div
          className={classNames('mediocre-tableEditable-axisCell-label', 'u-vertical-center-items', {
            'is-singleInput-header': isSingleInputHeader,
          })}
        >
          {isSingleInputHeader && renderDragHandle()}
          {prefixIcon}
          <span
            className={classNames(
              'mediocre-tableEditable-axisCell-label-text u-white-space-pre-wrap u-overflow-wrap-break-word',
              {
                'text-left': isLeftAlign,
                'mediocre-tableEditable-axisCell--header': isHeaderCell,
                'mediocre-tableEditable-axisCell--highlighted-content': shouldHighlightCell,
              }
            )}
            {...getTestProps(dataTestAttrs)}
          >
            {renderLabel(hasRowBeenInView)}
          </span>
          <div className="u-flex-display">{renderIcons()}</div>
          {hasContextOptions ? (
            <span
              ref={contextMenuButtonRef}
              className={classNames('mediocre-tableEditable-axisCell-dropdown u-vertical-center-items')}
              onClick={(e) => {
                handleOpenContextMenu(e);
              }}
              {...getTestProps({component: 'TableAxisCell', element: 'contextMenuButton'})}
            >
              <BenchlingIcon icon="chevron-down" />
            </span>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <td
      {...getTestProps({
        component: 'TableAxisCell',
        element: dim === TABLE_ROW ? 'rowheader' : 'columnheader',
        key: `${index}`,
      })}
      aria-selected={isSelected}
      role={dim === TABLE_ROW ? 'rowheader' : 'columnheader'}
      data-column-cid={dim === TABLE_ROW ? undefined : columnConstraint?.structuredTableColumn?.cid}
      className={classNames('mediocre-tableEditable-axisCell', {
        'mediocre-tableEditable-axisCell--row': dim === TABLE_ROW,
        'mediocre-tableEditable-axisCell--col': dim === TABLE_COLUMN,
        'is-selected': isSelected,
        'is-ghost-cell': isGhostCell,
        'mediocre-table-row-status--rowHasWarnings': rowProperties?.hasWarnings,
        'mediocre-table-row-status--rowIsOutdated': rowProperties?.isOutdated,
        'mediocre-table-row-status--rowHasUnsubmittedChanges': rowProperties?.hasUnsubmittedChanges,
        'mediocre-table-row-status--rowIsTombstoned': rowProperties?.isTombstoned,
        'mediocre-table-row-status--rowHasErrors': rowProperties?.hasErrors,
        'mediocre-table-row-status--rowIsArchived':
          rowProperties?.archivePurpose != null ||
          (rowProperties?.archivePurpose != null && rowProperties?.hasErrors),
        'mediocre-tableEditable-axisCell--columnIsOutdated': columnIsOutdated,
        'mediocre-tableEditable-axisCell--highlighted-background': shouldHighlightCell,
        'cell-sticky': isAffixed,
        'cell-rightShadow': showAffixedShadow,
        'cell-freezeBorder': isLastFrozenColumn,
        'cell-isFirstColumn': isFirstColumn,
        'cell-isSingleInputHeader': isSingleInputHeader,
        'cell-isSingleInputHeader-is-selected': isSingleInputHeader && isSelected,
      })}
      onMouseDown={handleCellDragStart}
      onMouseOver={handleCellDragOver}
      style={cellStyle}
      onContextMenu={(e) => {
        handleOpenContextMenu(e);
      }}
    >
      {prefixIcon != null && content}
      {prefixIcon == null && (
        // There is no tooltip on this cell's icon, so show it on the axis cell.
        // This can happen if column type icons are not enabled or if this is a row axis cell.
        <Tooltip
          text={
            // Provide a specific div for the tooltip text to widen it slightly
            <div className="mediocre-tableEditable-axisCell-tooltip-text">{tooltipTitle}</div>
          }
          isDisabled={!tooltipTitle}
          placement={FloatingPlacement.TOP}
          toggleDisplayMode={ToggleDisplayMode.BLOCK}
        >
          {content}
        </Tooltip>
      )}
    </td>
  );
}

const TableAxisCell = React.memo(TableAxisCellComponent);

const aphroditeStyles = StyleSheet.create({
  columnMappingIcon: {
    color: BLUE_LIGHT,
  },
});

export default TableAxisCell;
